<template>
    <div v-if="user" class="min-h-screen bg-gray-100">
      <SidebarMenu
        :user="user"
        :isMenuOpen="isMenuOpen"
        @toggle-menu="toggleMenu"
        @close-menu="closeMenu"
        @logout="logout"
      />
  
      <!-- Contenido Principal - ajustado para tener margen izquierdo en escritorio -->
      <div class="md:ml-64">
        <main class="p-4">
          <div class="p-4">
            <h1 class="text-xl font-bold">Inconformidades</h1>
            <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>
  
            <!-- Selector de periodos -->
            <PeriodSelector 
              v-if="studentId"
              :studentId="studentId"
              v-model="selectedPeriod"
              @period-changed="loadDisagreements"
            />
  
            <!-- Tabla de inconformidades -->
            <div v-if="loadingDisagreements" class="flex justify-center my-8">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
            
            <div v-else-if="errorDisagreements" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {{ errorDisagreements }}
            </div>
            
            <div v-else-if="disagreements && disagreements.length > 0">
              <h2 class="text-lg font-semibold mb-2">Inconformidades del periodo {{ selectedPeriod }}:</h2>
              
              <!-- Tabla responsiva para dispositivos móviles -->
              <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <div class="grid grid-cols-1 gap-4 sm:hidden">
                  <!-- Vista móvil: tarjetas individuales para cada inconformidad -->
                  <div 
                    v-for="(disagreement, index) in disagreements" 
                    :key="index"
                    class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4"
                    :class="getStatusBorderColor(disagreement.Estatus)"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <h3 class="font-bold text-lg">{{ disagreement.Materia }}</h3>
                    </div>
                    
                    <div class="space-y-2">
                      <div>
                        <span class="text-sm font-medium text-gray-500">Materia:</span>
                        <span class="text-sm ml-2">{{ disagreement.Materia }}</span>
                      </div>
                      
                      <div>
                        <span class="text-sm font-medium text-gray-500">Parcial:</span>
                        <p class="text-sm mt-1 text-gray-900">{{ disagreement.Parcial }}</p>
                      </div>

                      <div>
                        <span class="text-sm font-medium text-gray-500">Estatus:</span>
                        <p class="text-sm mt-1 text-gray-900"> {{ getStatusText(disagreement.Estatus) }}</p>
                      </div>
                      
                      <div>
                        <span class="text-sm font-medium text-gray-500">Inconformidad:</span>
                        <p class="text-sm mt-1 text-gray-900">{{ disagreement.Inconformidad }}</p>
                      </div>

                      <div v-if="disagreement.Respuesta">
                        <span class="text-sm font-medium text-gray-500">Respuesta:</span>
                        <p class="text-sm mt-1 text-gray-900">{{ disagreement.Respuesta }}</p>
                      </div>
                      
                      <div v-if="disagreement.Atendido">
                        <span class="text-sm font-medium text-gray-500">Atendido:</span>
                        <span class="text-sm ml-2">{{ disagreement.Atendido }}</span>
                      </div>

                      <!-- Botones de acción para móvil -->
                      <div class="col-span-2 mt-4 flex flex-col space-y-2">
                        <button @click="viewDetails(disagreement, index)"
                          class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          Ver más
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Vista escritorio: tabla tradicional -->
                <table class="min-w-full divide-y divide-gray-300 hidden sm:table">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Materia</th>
                      <!-- Parcial: oculto en pantallas medianas -->
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 hidden lg:table-cell">Parcial</th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Estatus</th>
                      <!-- Inconformidad: oculta en pantallas medianas y pequeñas -->
                      <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 hidden xl:table-cell">Inconformidad</th>
                      <!-- Respuesta: oculta en pantallas medianas y pequeñas -->
                      <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 hidden xl:table-cell">Respuesta</th>
                      <!-- Atendido: oculto en pantallas medianas -->
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 hidden lg:table-cell">Atendido</th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Opciones</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 bg-white">
                    <tr 
                        v-for="(disagreement, index) in disagreements" 
                        :key="index"
                    >
                      <td class="whitespace-normal py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                        {{ disagreement.Materia }}
                      </td>
                      <!-- Parcial: oculto en pantallas medianas -->
                      <td class="px-3 py-4 text-sm text-center hidden lg:table-cell">
                        {{ disagreement.Parcial }}
                      </td>
                      <td class="px-3 py-4 text-sm text-center">
                        {{ getStatusText(disagreement.Estatus)  }}
                      </td>
                      <!-- Inconformidad: oculta en pantallas medianas y pequeñas -->
                      <td class="px-3 py-4 text-sm text-gray-900 max-w-xs hidden xl:table-cell">
                        <div class="truncate" :title="disagreement.Inconformidad">
                          {{ disagreement.Inconformidad }}
                        </div>
                      </td>
                      <!-- Respuesta: oculta en pantallas medianas y pequeñas -->
                      <td class="px-3 py-4 text-sm text-gray-900 max-w-xs hidden xl:table-cell">
                        <div class="truncate" :title="disagreement.Respuesta">
                          {{ disagreement.Respuesta || '-' }}
                        </div>
                      </td>
                      <!-- Atendido: oculto en pantallas medianas -->
                      <td class="px-3 py-4 text-sm text-center hidden lg:table-cell">
                        {{ disagreement.Atendido ? disagreement.Atendido : '-' }}
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-center">
                        <div class="buttons-container">
                          <!-- Botón Ver detalles -->
                          <div class="tooltip-wrapper">
                            <button @click="viewDetails(disagreement, index)"
                              class="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                            </button>
                            <div class="tooltip tooltip-details">
                              <span class="tooltip-text">Ver detalles</span>
                              <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div v-else-if="selectedPeriod" class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
              Sin información disponible
            </div>
          </div>
        </main>
      </div>

      <!-- Modal Ver Detalles -->
      <DisagreementDetails
        v-if="showDetailsModal"
        :disagreement="selectedDisagreement"
        @close="closeDetailsModal"
      />
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import SidebarMenu from '@/components/SidebarMenu.vue';
  import PeriodSelector from '@/components/PeriodSelector.vue';
  import DisagreementDetails from '@/components/InconformidadDetallesModal.vue';
  import { getDisagreements } from "@/services/api";
  
  export default {
    components: {
      SidebarMenu,
      PeriodSelector,
      DisagreementDetails
    },
    data() {
      return {
        user: null,
        isMenuOpen: false,
        studentId: null,
        selectedPeriod: '',
        
        // Datos de inconformidades
        disagreements: null,
        loadingDisagreements: false,
        errorDisagreements: null,
        
        // Modal de detalles
        showDetailsModal: false,
        selectedDisagreement: null
      };
    },
    async created() {
      await this.getUserFromBackend();
    },
    methods: {
      async getUserFromBackend() {
        try {
          const response = await axios.get('/me');
          this.user = response.data.user;
          
          // Extraer el ID de estudiante
          if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
            this.studentId = this.user.email.match(/^a(\d+)@/)[1];
          } else {
            this.errorDisagreements = "No se pudo extraer el ID de estudiante del email.";
          }
        } catch (error) {
          console.error("Error obteniendo usuario", error);
          this.$router.push('/login');
        }
      },
      
      async loadDisagreements(periodId) {
        if (!periodId) return;
        
        this.loadingDisagreements = true;
        this.errorDisagreements = null;
        this.disagreements = null;
        
        try {
          const response = await getDisagreements({ 
            idstudent: this.studentId,
            idperiod: periodId
          });
          this.disagreements = response.data;
        } catch (error) {
          console.error("Error al obtener las inconformidades:", error);
          this.errorDisagreements = "Error al cargar las inconformidades. Por favor, intente nuevamente.";
        } finally {
          this.loadingDisagreements = false;
        }
      },
      
      viewDetails(disagreement, index) {
        this.selectedDisagreement = { ...disagreement, index };
        this.showDetailsModal = true;
      },
      
      closeDetailsModal() {
        this.showDetailsModal = false;
        this.selectedDisagreement = null;
      },
      
      getStatusText(status) {
        const statusMap = {
          'pending': 'Pendiente',
          'resolved': 'Resuelta'
        };
        return statusMap[status] || status;
      },
      
      getStatusBadgeColor(status) {
        const colorMap = {
          'pending': 'bg-yellow-100 text-yellow-800',
          'resolved': 'bg-green-100 text-green-800'
        };
        return colorMap[status] || 'bg-gray-100 text-gray-800';
      },
      
      getStatusIndicatorColor(status) {
        const colorMap = {
          'pending': 'bg-yellow-400',
          'resolved': 'bg-green-400'
        };
        return colorMap[status] || 'bg-gray-400';
      },
      
      getStatusBorderColor(status) {
        const colorMap = {
          'pending': 'border-yellow-500',
          'resolved': 'border-green-500'
        };
        return colorMap[status] || 'border-gray-500';
      },
      
      formatDate(dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      },
      
      toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
      },
      
      closeMenu() {
        this.isMenuOpen = false;
      },
      
      async logout() {
        try {
          // Obtener nuevo token CSRF
          await axios.get('/sanctum/csrf-cookie');
  
          // Esperar un momento para asegurar que la cookie se haya establecido
          await new Promise(resolve => setTimeout(resolve, 100));
  
          // Realizar la petición de logout
          const response = await axios.post('/logout', {}, {
            withCredentials: true,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          console.log(response);
          // Limpiar cookies locales
          document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
          });
          // Redirigir al login
          this.$router.push('/login');
        } catch (error) {
          console.error('Error al cerrar sesión:', error.response?.data || error);
        }
      }
    }
  };
  </script>